import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { managerComponentStyles } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/components/styles.ts';
import type { TApiReadyCovenant, TApiReadyCovenantDocument } from '~/modules/covenants/types.ts';
import { StatusChipWithTooltip } from '~/modules/covenants/components/StatusChipWithTooltip.tsx';
import { getReadyDocumentStatusConfig } from '../../utils/utils.ts';
import {
  MANAGER_READY_COVENANT_DOCUMENT_NAME_HEADER,
  MANAGER_READY_COVENANT_DOCUMENT_STATUS_HEADER,
  MANAGER_READY_COVENANT_LOAD_DOCUMENTS_BUTTON_TEXT,
  MANAGER_READY_COVENANT_LOAD_MORE_BUTTON_TEXT,
  MANAGER_READY_COVENANT_NO_RELATED_DOCUMENTS_TEXT,
} from '../../constants.ts';

interface IManagerReadyCovenantDocumentsSectionProps {
  covenant: TApiReadyCovenant;
  covenantKey: string;
  loadedDocuments: TApiReadyCovenantDocument[];
  loadedCount: number;
  totalDocumentIds: number;
  isDocumentsLoading: boolean;
  documentsErrorText?: string;
  onLoadDocuments: (covenant: TApiReadyCovenant) => void | Promise<void>;
}

export const ReadyCovenantDocumentsSection: React.FC<
  IManagerReadyCovenantDocumentsSectionProps
> = ({
  covenant,
  covenantKey,
  loadedDocuments,
  loadedCount,
  totalDocumentIds,
  isDocumentsLoading,
  documentsErrorText,
  onLoadDocuments,
}) => {
  const canLoadMore = totalDocumentIds > loadedCount;
  const loadDocumentsButtonVariant = canLoadMore ? 'contained' : 'text';

  return (
    <>
      {totalDocumentIds > 0 && !loadedDocuments.length && (
        <Box sx={managerComponentStyles.readyDocsLoadInitial}>
          <Button
            size="small"
            variant={loadDocumentsButtonVariant}
            sx={canLoadMore ? managerComponentStyles.tableActionBtn : undefined}
            onClick={() => {
              void onLoadDocuments(covenant);
            }}
            disabled={isDocumentsLoading || !canLoadMore}
          >
            {loadedCount === 0
              ? MANAGER_READY_COVENANT_LOAD_DOCUMENTS_BUTTON_TEXT
              : MANAGER_READY_COVENANT_LOAD_MORE_BUTTON_TEXT}
          </Button>
          {isDocumentsLoading && <CircularProgress size={16} />}
        </Box>
      )}

      {totalDocumentIds === 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={managerComponentStyles.readyNoDocsText}
        >
          {MANAGER_READY_COVENANT_NO_RELATED_DOCUMENTS_TEXT}
        </Typography>
      )}

      {documentsErrorText && (
        <Typography
          variant="caption"
          color="error"
          sx={managerComponentStyles.readyDocsErrorText}
        >
          {documentsErrorText}
        </Typography>
      )}

      {loadedDocuments.length > 0 && (
        <TableContainer sx={managerComponentStyles.readyDocsTable}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{MANAGER_READY_COVENANT_DOCUMENT_NAME_HEADER}</TableCell>
                <TableCell>{MANAGER_READY_COVENANT_DOCUMENT_STATUS_HEADER}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadedDocuments.map((document) => {
                const documentStatusConfig = getReadyDocumentStatusConfig(document.status);

                return (
                  <TableRow key={`${covenantKey}-${document.id}`}>
                    <TableCell sx={managerComponentStyles.readyDocNameCell}>
                      {document.displayName}
                    </TableCell>
                    <TableCell>
                      <StatusChipWithTooltip
                        label={documentStatusConfig.label}
                        color={documentStatusConfig.color}
                        description={documentStatusConfig.description}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {totalDocumentIds > 0 && (
            <Box sx={managerComponentStyles.readyDocsLoadBox}>
              <Button
                size="small"
                variant={loadDocumentsButtonVariant}
                sx={canLoadMore ? managerComponentStyles.tableActionBtn : undefined}
                onClick={() => {
                  void onLoadDocuments(covenant);
                }}
                disabled={isDocumentsLoading || !canLoadMore}
              >
                {loadedCount === 0
                  ? MANAGER_READY_COVENANT_LOAD_DOCUMENTS_BUTTON_TEXT
                  : MANAGER_READY_COVENANT_LOAD_MORE_BUTTON_TEXT}
              </Button>
              {isDocumentsLoading && <CircularProgress size={16} />}
            </Box>
          )}
        </TableContainer>
      )}
    </>
  );
};


